import React, { useState, useEffect } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { 
  Card, 
  Table, 
  Row, 
  Col, 
  Form, 
  InputGroup, 
  Button, 
  Pagination, 
  Badge, 
  Modal, 
  Toast,
  ToastContainer,
  Spinner,
  ProgressBar,
  FloatingLabel
} from "react-bootstrap";
import axios from "axios";
import {
  Search,
  Eye,
  Pencil,
  Trash,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  ExclamationCircle,
  Layers,
  Activity,
  Tag,
  List,
  Folder,
  FolderPlus,
  Repeat,
  Cloud,
  BarChartFill,
  Globe,
  Building,
  Shop
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../constants/api_constants";

// Configuration de l'API

const CATEGORIES_ENDPOINT = `${API_BASE_URL}/taxes-categories`;

export default function CategoriesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // ✅ Récupérer l'id_marche depuis l'objet user
  const userMarcheId = user?.id_marche;
  const roleId = user?.role_id;
  
  // ✅ Vérifier si l'utilisateur a un marché OU est Admin Principal (1003)
  const hasMarket = !!userMarcheId && userMarcheId !== 'null' && userMarcheId !== 'undefined';
  const isAdminPrincipal = roleId === 1003;
  
  // ✅ L'utilisateur est considéré comme un "market user" s'il a un marché OU s'il est Admin Principal
  const isMarketUser = hasMarket || isAdminPrincipal;
  
  // ✅ Déterminer les libellés en fonction du type d'utilisateur
  const labels = isMarketUser ? {
    title: "Catégories de redevances",
    pageTitle: "Gestion des Catégories de Redevances",
    description: "Consultez et gérez toutes les catégories de redevances",
    breadcrumb: "Catégories de redevances",
    list: "Liste des Catégories de redevances",
    new: "Nouvelle Catégorie",
    edit: "Modifier la catégorie de redevance",
    details: "Détails de la catégorie de redevance",
    delete: "Confirmer la suppression",
    deleteConfirm: "Êtes-vous sûr de vouloir supprimer cette catégorie de redevance ?",
    deleteSuccess: "Catégorie de redevance supprimée avec succès",
    addSuccess: "Catégorie de redevance ajoutée avec succès",
    updateSuccess: "Catégorie de redevance modifiée avec succès",
    nameLabel: "Nom de la catégorie de redevance *",
    descLabel: "Description de la catégorie de redevance",
    namePlaceholder: "Nom de la catégorie de redevance",
    descPlaceholder: "Description de la catégorie de redevance (optionnelle)",
    noData: "AUCUNE CATÉGORIE DE REDEVANCE TROUVÉE",
    noDataDesc: "Aucune catégorie de redevance ne correspond à vos critères",
    categoryLabel: "Catégorie",
    idLabel: "ID",
    createdLabel: "Créée le",
    actionsLabel: "Actions",
    searchPlaceholder: "Rechercher par nom ou description...",
    filterActive: "Filtre actif",
    selectedCount: "sélectionné(s)",
    loadingText: "Chargement des catégories de redevances...",
    addCategory: "Ajouter une catégorie de redevance",
    editCategory: "Modifier la catégorie de redevance",
    viewDetails: "Voir les détails",
    noDescription: "Aucune description fournie",
    confirmDeleteText: "Cette action est irréversible.",
    cancel: "Annuler",
    save: "Enregistrer",
    delete: "Supprimer",
    retry: "Réessayer",
    reset: "Réinitialiser",
    refresh: "Rafraîchir",
    close: "Fermer",
    categoryRequired: "Le nom de la catégorie de redevance est requis",
    categoryName: "Nom de la catégorie de redevance",
    description: "Description",
    createdOn: "Créée le",
    unknown: "Inconnu",
    statsTitle: "Statistiques",
    totalCategories: "Total Catégories",
    withDescription: "Avec Description",
    descriptionRate: "Taux de description",
    categoryDeleted: "Catégorie supprimée",
    categoryAdded: "Catégorie ajoutée",
    categoryUpdated: "Catégorie modifiée",
    errorLoading: "Erreur de chargement",
    errorDeleting: "Erreur lors de la suppression",
    errorAdding: "Erreur lors de l'ajout",
    errorUpdating: "Erreur lors de la modification",
    noDescriptionShort: "Aucune description",
    filterReset: "Réinitialiser la recherche",
    showing: "Affichage de",
    to: "à",
    of: "sur",
    categories: "catégories",
    selected: "sélectionné(s)",
    perPage: "/ page",
  } : {
    title: "Catégories de Taxes",
    pageTitle: "Gestion des Catégories de Taxes",
    description: "Consultez et gérez toutes les catégories de taxes",
    breadcrumb: "Catégories de Taxes",
    list: "Liste des Catégories de Taxes",
    new: "Nouvelle Catégorie",
    edit: "Modifier la catégorie",
    details: "Détails de la catégorie",
    delete: "Confirmer la suppression",
    deleteConfirm: "Êtes-vous sûr de vouloir supprimer cette catégorie ?",
    deleteSuccess: "Catégorie supprimée avec succès",
    addSuccess: "Catégorie ajoutée avec succès",
    updateSuccess: "Catégorie modifiée avec succès",
    nameLabel: "Nom de la catégorie *",
    descLabel: "Description",
    namePlaceholder: "Nom de la catégorie",
    descPlaceholder: "Description de la catégorie (optionnelle)",
    noData: "AUCUNE CATÉGORIE TROUVÉE",
    noDataDesc: "Aucune catégorie ne correspond à vos critères",
    categoryLabel: "Catégorie",
    idLabel: "ID",
    createdLabel: "Créée le",
    actionsLabel: "Actions",
    searchPlaceholder: "Rechercher par nom ou description...",
    filterActive: "Filtre actif",
    selectedCount: "sélectionné(s)",
    loadingText: "Chargement des catégories...",
    addCategory: "Ajouter une catégorie",
    editCategory: "Modifier la catégorie",
    viewDetails: "Voir les détails",
    noDescription: "Aucune description fournie",
    confirmDeleteText: "Cette action est irréversible.",
    cancel: "Annuler",
    save: "Enregistrer",
    delete: "Supprimer",
    retry: "Réessayer",
    reset: "Réinitialiser",
    refresh: "Rafraîchir",
    close: "Fermer",
    categoryRequired: "Le nom de la catégorie est requis",
    categoryName: "Nom de la catégorie",
    description: "Description",
    createdOn: "Créée le",
    unknown: "Inconnu",
    statsTitle: "Statistiques",
    totalCategories: "Total Catégories",
    withDescription: "Avec Description",
    descriptionRate: "Taux de description",
    categoryDeleted: "Catégorie supprimée",
    categoryAdded: "Catégorie ajoutée",
    categoryUpdated: "Catégorie modifiée",
    errorLoading: "Erreur de chargement",
    errorDeleting: "Erreur lors de la suppression",
    errorAdding: "Erreur lors de l'ajout",
    errorUpdating: "Erreur lors de la modification",
    noDescriptionShort: "Aucune description",
    filterReset: "Réinitialiser la recherche",
    showing: "Affichage de",
    to: "à",
    of: "sur",
    categories: "catégories",
    selected: "sélectionné(s)",
    perPage: "/ page",
  };

  // États principaux
  const [categories, setCategories] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [sortField, setSortField] = useState('category_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États pour les modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // États pour les données des modals
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editedCategory, setEditedCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [newCategory, setNewCategory] = useState({
    category_name: "",
    category_descr: ""
  });
  
  // États pour les chargements
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  
  // États pour les notifications
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  // Obtenir le token d'authentification
  const getAuthToken = () => sessionStorage.getItem("authToken");

  // Headers pour les requêtes API
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  // Charger les catégories depuis l'API
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(CATEGORIES_ENDPOINT, {
        headers: getHeaders(),
      });
      
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setCategories(response.data.data);
      } else if (Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        setCategories([]);
      }
    } catch (err) {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Initialiser les données
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fonctions pour les notifications
  const showAlert = (message, type = 'info', duration = 4000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) {
      setTimeout(() => {
        setAlertMessage(prev => ({ ...prev, show: false }));
      }, duration);
    }
  };

  const hideAlert = () => {
    setAlertMessage(prev => ({ ...prev, show: false }));
  };

  const handleRetry = () => {
    setError(null);
    fetchCategories();
  };

  // Filtrage et tri des données
  const filteredAndSortedItems = React.useMemo(() => {
    let filtered = [...categories];
    
    if (filterText) {
      const searchTerm = filterText.toLowerCase();
      filtered = filtered.filter(item => 
        (item.category_name && item.category_name.toLowerCase().includes(searchTerm)) ||
        (item.category_descr && item.category_descr.toLowerCase().includes(searchTerm))
      );
    }

    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (sortField === 'category_name') {
        aValue = a.category_name || '';
        bValue = b.category_name || '';
      } else if (sortField === 'created_at') {
        aValue = a.created_at || '';
        bValue = b.created_at || '';
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [categories, filterText, sortField, sortDirection]);

  // Statistiques
  const stats = React.useMemo(() => {
    const total = categories.length;
    const withDescription = categories.filter(c => c.category_descr && c.category_descr.trim() !== '').length;
    const descriptionRate = total > 0 ? Math.round((withDescription / total) * 100) : 0;
    
    return { 
      total, 
      withDescription,
      descriptionRate
    };
  }, [categories]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  // Fonctions de tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Gestion des sélections
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCategories(currentItems.map(category => category.category_id || category.id));
    } else {
      setSelectedCategories([]);
    }
  };

  const handleSelectCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const isAllSelected = currentItems.length > 0 && 
    selectedCategories.length === currentItems.length;

  const isSomeSelected = selectedCategories.length > 0 && 
    selectedCategories.length < currentItems.length;

  // Fonctions de formatage
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Obtenir la couleur du badge basée sur l'ID
  const getCategoryColor = (id) => {
    const colors = ["primary", "success", "info", "warning", "dark", "secondary"];
    return colors[Math.abs(id) % colors.length];
  };

  // Obtenir le badge de catégorie
  const getCategoryBadge = (category) => {
    const color = getCategoryColor(category.id || 0);
    return (
      <Badge bg={color} className="px-3 py-2 rounded-pill">
        {category.category_name}
      </Badge>
    );
  };

  // Gestion de la suppression
  const handleDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      setLoadingDelete(true);
      
      await axios.delete(`${CATEGORIES_ENDPOINT}/${categoryToDelete.category_id || categoryToDelete.id}`, {
        headers: getHeaders(),
      });

      setCategories(prev => prev.filter(c => 
        (c.category_id || c.id) !== (categoryToDelete.category_id || categoryToDelete.id)
      ));
      setSelectedCategories(prev => prev.filter(id => id !== (categoryToDelete.id || categoryToDelete.category_id)));
      showAlert(labels.deleteSuccess, 'success');
    } catch (error) {
      showAlert(`${labels.errorDeleting}: ${error.message}`, 'danger');
    } finally {
      setLoadingDelete(false);
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };

  // Gestion de la modification
  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setEditedCategory({...category});
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editedCategory) return;

    try {
      setLoadingUpdate(true);
      
      await axios.put(
        `${CATEGORIES_ENDPOINT}/${editedCategory.category_id || editedCategory.id}`,
        {
          category_name: editedCategory.category_name,
          category_descr: editedCategory.category_descr
        },
        { headers: getHeaders() }
      );

      setCategories(prev => prev.map(c => 
        (c.category_id || c.id) === (editedCategory.category_id || editedCategory.id) ? editedCategory : c
      ));
      
      setShowEditModal(false);
      showAlert(labels.updateSuccess, 'success');
    } catch (error) {
      showAlert(`${labels.errorUpdating}: ${error.message}`, 'danger');
    } finally {
      setLoadingUpdate(false);
    }
  };

  // Gestion de l'ajout
  const handleAddCategory = async () => {
    if (!newCategory.category_name.trim()) {
      showAlert(labels.categoryRequired, 'warning');
      return;
    }

    try {
      setLoadingAdd(true);
      
      const response = await axios.post(
        CATEGORIES_ENDPOINT,
        newCategory,
        { headers: getHeaders() }
      );

      setCategories(prev => [...prev, response.data.data || response.data]);
      
      setShowAddModal(false);
      setNewCategory({
        category_name: "",
        category_descr: ""
      });

      await fetchCategories();
      showAlert(labels.addSuccess, 'success');
    } catch (error) {
      showAlert(`${labels.errorAdding}: ${error.message}`, 'danger');
    } finally {
      setLoadingAdd(false);
    }
  };

  // Voir les détails
  const handleViewDetails = (category) => {
    setSelectedCategory(category);
    setShowDetailsModal(true);
  };

  // Composant pour l'en-tête triable
  const SortableHeader = ({ field, children }) => (
    <th 
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(field)}
      className="position-relative"
    >
      {children}
      {sortField === field && (
        <span className="ms-1">
          {sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      )}
    </th>
  );

  // Fonction pour rafraîchir les données
  const handleRefresh = () => {
    fetchCategories();
  };

  return (
    <React.Fragment>
      <Header />
      
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={alertMessage.show} onClose={hideAlert} bg={alertMessage.type} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            {alertMessage.type === 'success' && <CheckCircle className="me-2 text-success" />}
            {alertMessage.type === 'danger' && <XCircle className="me-2 text-danger" />}
            {alertMessage.type === 'warning' && <ExclamationCircle className="me-2 text-warning" />}
            {alertMessage.type === 'info' && <CheckCircle className="me-2 text-info" />}
            <strong className="me-auto">Notification</strong>
            <small>maintenant</small>
          </Toast.Header>
          <Toast.Body className="text-white fw-medium">{alertMessage.message}</Toast.Body>
        </Toast>
      </ToastContainer>
      
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <div className="container-fluid">
          {/* En-tête */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex flex-wrap justify-content-between align-items-center">
                <div className="mb-3 mb-md-0">
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><Link to="/admin/dashboard">Tableau de bord</Link></li>
                    <li className="breadcrumb-item active" aria-current="page">{labels.breadcrumb}</li>
                  </ol>
                  <h4 className="main-title mb-0 d-flex align-items-center">
                    <Folder className="me-2 text-primary" size={28} />
                    {labels.pageTitle}
                  </h4>
                  <p className="text-muted mb-0">{labels.description}</p>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  {error && (
                    <Button variant="warning" size="sm" onClick={handleRefresh} className="d-flex align-items-center">
                      <ExclamationCircle className="me-2" />{labels.retry}
                    </Button>
                  )}
                  
                  <Button variant="success" onClick={() => setShowAddModal(true)} className="d-flex align-items-center shadow-sm">
                    <FolderPlus className="me-2" size={16} />{labels.new}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Barre de recherche */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-3">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <InputGroup>
                        <InputGroup.Text className="bg-light border-end-0">
                          <Search className="text-primary" />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder={labels.searchPlaceholder}
                          value={filterText}
                          onChange={(e) => setFilterText(e.target.value)}
                          className="border-start-0 bg-light"
                        />
                        {filterText && (
                          <Button variant="outline-secondary" className="border-start-0" onClick={() => setFilterText('')}>
                            <X size={16} />
                          </Button>
                        )}
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <Form.Select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="bg-light"
                      >
                        <option value="5">5 {labels.perPage}</option>
                        <option value="10">10 {labels.perPage}</option>
                        <option value="25">25 {labels.perPage}</option>
                        <option value="50">50 {labels.perPage}</option>
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          onClick={() => {
                            setFilterText("");
                            setCurrentPage(1);
                            setSelectedCategories([]);
                          }}
                          className="flex-grow-1 d-flex align-items-center justify-content-center"
                        >
                          <X className="me-2" size={16} />{labels.reset}
                        </Button>
                        <Button
                          variant="outline-primary"
                          onClick={handleRefresh}
                          className="d-flex align-items-center justify-content-center"
                          disabled={loading}
                        >
                          <Cloud size={16} />
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tableau principal */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded-3">
                <Card.Header className="bg-primary d-flex justify-content-between align-items-center py-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <Folder className="me-2 text-white" />
                    <span className="fw-semibold text-white">{labels.list} ({filteredAndSortedItems.length})</span>
                    {filterText && <Badge bg="info" className="ms-2 rounded-pill">{labels.filterActive}</Badge>}
                  </div>
                  <div className="text-white small d-flex align-items-center">
                    {selectedCategories.length > 0 && (
                      <span className="me-3">{selectedCategories.length} {labels.selectedCount}</span>
                    )}
                  </div>
                </Card.Header>
                
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              style={{ borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer' }}
                              checked={isAllSelected}
                              ref={input => { 
                                if (input) {
                                  input.indeterminate = isSomeSelected;
                                }
                              }}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th>{labels.idLabel}</th>
                          <SortableHeader field="category_name">{labels.categoryName}</SortableHeader>
                          <th>{labels.description}</th>
                          <th>{labels.createdOn}</th>
                          <th style={{ width: '130px' }} className="text-center">{labels.actionsLabel}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading && categories.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center py-5">
                              <Spinner animation="border" variant="primary" />
                              <p className="mt-2 text-muted">{labels.loadingText}</p>
                            </td>
                          </tr>
                        ) : error && categories.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center py-5">
                              <ExclamationCircle size={48} className="text-danger mb-3" />
                              <h5>{labels.errorLoading}</h5>
                              <p>{error}</p>
                              <Button variant="primary" onClick={handleRefresh} className="mt-3">{labels.retry}</Button>
                            </td>
                          </tr>
                        ) : currentItems.length > 0 ? (
                          currentItems.map((category, index) => {
                            const categoryId = category.category_id || category.id;
                            const categoryName = category.category_name || category.name;
                            
                            return (
                              <tr key={categoryId}>
                                <td>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    style={{ borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer' }}
                                    checked={selectedCategories.includes(categoryId)}
                                    onChange={() => handleSelectCategory(categoryId)}
                                  />
                                </td>
                                <td>
                                  <Badge bg="light" text="dark" className="fw-semibold">
                                    {categoryId || index + 1}
                                  </Badge>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <Folder size={16} className="text-primary me-2" />
                                    <strong className="d-block">{categoryName}</strong>
                                  </div>
                                </td>
                                <td>
                                  <span className="text-muted small">
                                    {category.category_descr 
                                      ? (category.category_descr.length > 60 
                                          ? `${category.category_descr.substring(0, 60)}...` 
                                          : category.category_descr)
                                      : labels.noDescriptionShort}
                                  </span>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {formatDate(category.created_at)}
                                  </small>
                                </td>
                                <td>
                                  <div className="d-flex gap-1 justify-content-center">
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      title={labels.viewDetails}
                                      onClick={() => handleViewDetails(category)}
                                      className="rounded-circle"
                                      style={{ width: '34px', height: '34px', padding: 0 }}
                                    >
                                      <Eye size={14} />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-info"
                                      title={labels.editCategory}
                                      onClick={() => handleEditCategory(category)}
                                      className="rounded-circle"
                                      style={{ width: '34px', height: '34px', padding: 0 }}
                                    >
                                      <Pencil size={14} />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center py-5">
                              <Folder size={48} className="text-muted opacity-25" />
                              <h5 className="mt-3 text-muted">{labels.noData}</h5>
                              <p className="text-muted small">{labels.noDataDesc}</p>
                              {filterText && (
                                <Button variant="outline-primary" size="sm" onClick={() => setFilterText('')}>
                                  {labels.filterReset}
                                </Button>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {totalPages > 1 && (
                    <div className="p-3 border-top d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
                      <div className="text-muted small">
                        {labels.showing} <strong>{startIndex + 1}</strong> {labels.to} <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> {labels.of} <strong>{filteredAndSortedItems.length}</strong> {labels.categories}
                        {selectedCategories.length > 0 && (
                          <span className="ms-2 text-primary">• {selectedCategories.length} {labels.selected}</span>
                        )}
                      </div>
                      <Pagination className="mb-0">
                        <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} />
                        {[...Array(Math.min(totalPages, 7))].map((_, index) => {
                          const pageNum = index + 1;
                          return (
                            <Pagination.Item key={pageNum} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)} className={pageNum === currentPage ? 'fw-bold' : ''}>
                              {pageNum}
                            </Pagination.Item>
                          );
                        })}
                        {totalPages > 7 && (
                          <>
                            <Pagination.Ellipsis />
                            <Pagination.Item onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>
                          </>
                        )}
                        <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} />
                      </Pagination>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* MODAL D'AJOUT */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered size="lg">
          <Modal.Header closeButton className="text-white" style={{ background: "linear-gradient(135deg, #198754, #157347)" }}>
            <Modal.Title className="d-flex align-items-center">
              <FolderPlus className="me-2" />{labels.addCategory}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="g-3">
                <Col md={12}>
                  <FloatingLabel label={labels.nameLabel} className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder={labels.namePlaceholder}
                      value={newCategory.category_name}
                      onChange={(e) => setNewCategory({ ...newCategory, category_name: e.target.value })}
                    />
                  </FloatingLabel>
                </Col>
                <Col md={12}>
                  <FloatingLabel label={labels.descLabel} className="mb-3">
                    <Form.Control
                      as="textarea"
                      style={{ height: '120px' }}
                      placeholder={labels.descPlaceholder}
                      value={newCategory.category_descr}
                      onChange={(e) => setNewCategory({ ...newCategory, category_descr: e.target.value })}
                    />
                  </FloatingLabel>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={loadingAdd}>
              {labels.cancel}
            </Button>
            <Button variant="success" onClick={handleAddCategory} disabled={loadingAdd || !newCategory.category_name.trim()}>
              {loadingAdd ? <><Spinner size="sm" className="me-2" />{labels.save}...</> : <><Check className="me-2" />{labels.save}</>}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL D'ÉDITION */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
          <Modal.Header closeButton className="text-white" style={{ background: "linear-gradient(135deg, #0dcaf0, #0b5ed7)" }}>
            <Modal.Title className="d-flex align-items-center">
              <Pencil size={20} className="me-2" />{labels.editCategory}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4 py-3">
            {editedCategory && (
              <Form>
                <Row className="g-4">
                  <Col md={12}>
                    <FloatingLabel label={labels.nameLabel} className="mb-3">
                      <Form.Control
                        type="text"
                        placeholder={labels.namePlaceholder}
                        value={editedCategory.category_name || editedCategory.name || ''}
                        onChange={(e) => setEditedCategory({ ...editedCategory, category_name: e.target.value, name: e.target.value })}
                      />
                    </FloatingLabel>
                  </Col>
                  <Col md={12}>
                    <FloatingLabel label={labels.descLabel} className="mb-3">
                      <Form.Control
                        as="textarea"
                        style={{ height: '120px' }}
                        placeholder={labels.descPlaceholder}
                        value={editedCategory.category_descr || ''}
                        onChange={(e) => setEditedCategory({ ...editedCategory, category_descr: e.target.value })}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer className="px-4">
            <Button variant="outline-secondary" onClick={() => setShowEditModal(false)} disabled={loadingUpdate}>
              {labels.cancel}
            </Button>
            <Button variant="info" className="text-white" onClick={handleSaveEdit} disabled={loadingUpdate || !(editedCategory?.category_name || editedCategory?.name)?.trim()}>
              {loadingUpdate ? <><Spinner size="sm" className="me-2" />{labels.save}...</> : <><Check size={18} className="me-2" />{labels.save}</>}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL DE DÉTAILS */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered size="lg">
          <Modal.Header closeButton className="border-0 text-white" style={{ background: "linear-gradient(135deg, #0d6efd, #0b5ed7)" }}>
            <Modal.Title className="d-flex align-items-center">
              <Eye className="me-2" size={20} />{labels.details}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-light">
            {selectedCategory && (
              <>
                <div className="mb-4 text-center">
                  <Folder size={48} className="text-primary" />
                  <h4 className="fw-bold mb-1 mt-2">{selectedCategory.category_name || selectedCategory.name}</h4>
                  {getCategoryBadge(selectedCategory)}
                </div>
                <Row className="g-3">
                  <Col md={6}>
                    <Card className="border-0 shadow-sm h-100">
                      <Card.Body>
                        <div className="d-flex align-items-center mb-2">
                          <Tag className="text-primary me-2" />
                          <span className="fw-semibold">{labels.idLabel}</span>
                        </div>
                        <h5 className="mb-0">{selectedCategory.category_id || selectedCategory.id}</h5>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="border-0 shadow-sm h-100">
                      <Card.Body>
                        <div className="d-flex align-items-center mb-2">
                          <Activity className="text-info me-2" />
                          <span className="fw-semibold">{labels.createdOn}</span>
                        </div>
                        <span>{formatDate(selectedCategory.created_at)}</span>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={12}>
                    <Card className="border-0 shadow-sm">
                      <Card.Body>
                        <div className="d-flex align-items-center mb-2">
                          <List className="text-muted me-2" />
                          <span className="fw-semibold">{labels.description}</span>
                        </div>
                        <p className="text-muted mb-0">
                          {selectedCategory.category_descr || labels.noDescription}
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>{labels.close}</Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL DE SUPPRESSION */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title><ExclamationCircle className="text-warning me-2" />{labels.delete}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <Trash className="display-4 text-danger mb-3" size={48} />
              <h5>{labels.deleteConfirm}</h5>
              {categoryToDelete && (
                <div className="mt-3 p-3 bg-light rounded">
                  <strong>{categoryToDelete.category_name || categoryToDelete.name}</strong>
                  <br />
                  <small className="text-muted">{labels.idLabel}: {categoryToDelete.category_id || categoryToDelete.id}</small>
                  <div className="mt-2">{getCategoryBadge(categoryToDelete)}</div>
                </div>
              )}
              <p className="text-muted mt-3">{labels.confirmDeleteText}</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={loadingDelete}>
              <X className="me-2" />{labels.cancel}
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={loadingDelete}>
              {loadingDelete ? <><Spinner animation="border" size="sm" className="me-2" />{labels.delete}...</> : <><Trash className="me-2" />{labels.delete}</>}
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card { transition: transform 0.3s ease, box-shadow 0.3s ease; border-radius: 12px; overflow: hidden; }
          .stats-card:hover { transform: translateY(-5px); box-shadow: 0 10px 30px rgba(0,0,0,0.1) !important; }
          .card-gradient { position: absolute; top: 0; left: 0; width: 100%; height: 4px; opacity: 0.8; }
          .rounded-circle { border-radius: 50% !important; }
        `}</style>
        <Footer />
      </div>
    </React.Fragment>
  );
}